import { useMemo } from 'react';
import {
  useStoreCategories,
  useWebsiteReadinessV3,
  type StorefrontDestination,
  type SupabaseClient,
  type WebsiteReadinessReport,
} from '@dukanti/shared';
import { useProducts } from '@/hooks/use-products';

export interface UseDestinationAvailabilityArgs {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly templateId: string;
  readonly slotId: string;
  readonly link: StorefrontDestination;
}

export interface DestinationAvailability {
  /** PD10: the server reports the stored target of this slot as `invalid_reference`. */
  readonly unavailable: boolean;
  /** The name of the picked category/product from the store's own lists, or null. */
  readonly selectedLabel: string | null;
}

/** The slots whose stored reference the server says is gone (PD10: `invalid_reference` only). */
export function invalidReferenceSlotIds(
  report: WebsiteReadinessReport | undefined,
): ReadonlySet<string> {
  const ids = new Set<string>();
  if (!report) return ids;
  for (const issue of [...report.blocking, ...report.warnings]) {
    if (issue.code === 'invalid_reference' && issue.slotId !== undefined) ids.add(issue.slotId);
  }
  return ids;
}

/**
 * Epic #228 P5 (PD10): what the destination editor of one link slot knows
 * about its stored target. "Unavailable" comes from the template's readiness
 * (the same f075 eligibility the pickers and the renderer use), never from a
 * client guess. The name of the pick comes from the store's own lists, which
 * still name a hidden category or an unpublished product. Only the list of
 * the picked kind loads.
 */
export function useDestinationAvailability({
  client,
  storeId,
  templateId,
  slotId,
  link,
}: UseDestinationAvailabilityArgs): DestinationAvailability {
  const readiness = useWebsiteReadinessV3(client, storeId, templateId);
  const categories = useStoreCategories(client, link.kind === 'category' ? storeId : null);
  const products = useProducts(link.kind === 'product' ? storeId : null);

  const unavailableIds = useMemo(() => invalidReferenceSlotIds(readiness.data), [readiness.data]);

  const selectedLabel = useMemo((): string | null => {
    if (link.kind === 'category') {
      return categories.categories.find((row) => row.id === link.categoryId)?.name ?? null;
    }
    if (link.kind === 'product') {
      return (products.data ?? []).find((row) => row.id === link.productId)?.name ?? null;
    }
    return null;
  }, [categories.categories, link, products.data]);

  return { unavailable: unavailableIds.has(slotId), selectedLabel };
}
